import {
  ArrowDownward,
  ArrowUpward,
  Close,
  ContentCopy,
  DeleteOutline,
  FormatAlignCenter,
  FormatAlignLeft,
  FormatAlignRight,
  Height,
  HorizontalRule,
  ImageOutlined,
  Notes,
  Redo,
  SmartButton,
  Title,
  Undo,
  ViewSidebar,
} from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Paper, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import MediaLibrary from "../../components/ui/MediaLibrary";
import VariablePalette, { useFieldFocus } from "../../components/ui/VariablePalette";
import { MailBlock, MailBlockType, MailTemplate } from "../../service/MarketingService";
import { clientWidth, clippedByGmail, isPhone, MAIL_CLIENTS, MailClient } from "./mailClients";

/**
 * The mail editor, built the way the poster designer in BedstModWest works: the mail itself is the canvas. The
 * server renders the mail exactly as it will be sent, and the editor lays a box over each element (the server marks
 * them with data-block and data-row in the preview). The boxes are dragged to move an element, dragged by their
 * edges to make it narrower or wider, and the settings of the chosen element sit in a panel on the right. The grid
 * has 12 columns; elements that share a row stand side by side, and on a phone they fall under each other.
 */

const COLUMNS = 12;
const WIDTHS = [12, 8, 6, 4, 3];
const ELEMENTS: { type: MailBlockType; icon: JSX.Element }[] = [
  { type: "heading", icon: <Title fontSize="small" /> },
  { type: "text", icon: <Notes fontSize="small" /> },
  { type: "image", icon: <ImageOutlined fontSize="small" /> },
  { type: "imageText", icon: <ViewSidebar fontSize="small" /> },
  { type: "button", icon: <SmartButton fontSize="small" /> },
  { type: "divider", icon: <HorizontalRule fontSize="small" /> },
  { type: "spacer", icon: <Height fontSize="small" /> },
];
const VARIABLES = ["FIRSTNAME", "LASTNAME", "COMPANY", "EMAIL", "LOGIN"];
const BLOCK_KIND = "application/x-breefer-block";
const ORANGE = "#EB6608";

interface Placed extends MailBlock {
  row: number;
  x: number;
  w: number;
}
interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}
/** What was measured in the rendered mail: where the grid, the rows and the blocks are */
interface Layout {
  height: number;
  grid: { left: number; width: number; top: number; bottom: number };
  blocks: Record<number, Rect>;
  rows: Rect[];
}
/** Where a dragged element would land: in a row at a column, or on the line between two rows */
type Target = { row: number; x: number; w: number } | { line: number };

const newBlock = (type: MailBlockType, t: (key: string) => string): MailBlock => {
  const block: MailBlock = { type, align: "left", x: 0, w: COLUMNS };
  if (type === "heading") block.text = t("mails.blockExamples.heading");
  if (type === "text") block.text = t("mails.blockExamples.text");
  if (type === "imageText") {
    block.text = t("mails.blockExamples.text");
    block.size = "left";
  }
  if (type === "button") {
    block.text = t("mails.blockExamples.button");
    block.url = "|*LOGIN*|";
  }
  if (type === "spacer") block.size = "medium";
  return block;
};

/** A template written before this editor is read as blocks, the same way the server reads it */
export const blocksOf = (template: MailTemplate): MailBlock[] => {
  if (template.blocks?.length) return template.blocks;
  const blocks: MailBlock[] = [];
  if (template.imageUrl) blocks.push({ type: "image", imageUrl: template.imageUrl });
  if (template.headline) blocks.push({ type: "heading", text: template.headline });
  String(template.body || "")
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((part) => blocks.push({ type: "text", text: part }));
  if (template.buttonText && template.buttonUrl) blocks.push({ type: "button", text: template.buttonText, url: template.buttonUrl });
  return blocks;
};

/** Every block gets a place: a block from before the grid is a full-width row of its own. Rows are numbered 0, 1, 2 ... */
export const place = (blocks: MailBlock[]): Placed[] => {
  const placed = blocks.map((block, index) => {
    const w = Math.min(COLUMNS, Math.max(1, Number(block.w) || COLUMNS));
    const x = Math.min(COLUMNS - w, Math.max(0, Number(block.x) || 0));
    const row = block.row === undefined || block.row === null || !Number.isFinite(Number(block.row)) ? index : Number(block.row);
    return { ...block, row, x, w };
  });
  const rowNumbers = Array.from(new Set(placed.map((block) => block.row))).sort((a, b) => a - b);
  return placed.map((block) => ({ ...block, row: rowNumbers.indexOf(block.row) })).sort((a, b) => a.row - b.row || a.x - b.x);
};

/** Where in the row a block of width `w` fits closest to column `x`; null when the row has no room */
const fitInRow = (others: Placed[], x: number, w: number): number | null => {
  const taken: boolean[] = new Array(COLUMNS).fill(false);
  others.forEach((block) => {
    for (let column = block.x; column < block.x + block.w; column += 1) taken[column] = true;
  });
  const free = (from: number) => from >= 0 && from + w <= COLUMNS && taken.slice(from, from + w).every((cell) => !cell);
  const want = Math.min(COLUMNS - w, Math.max(0, x));
  for (let distance = 0; distance < COLUMNS; distance += 1) {
    if (free(want - distance)) return want - distance;
    if (free(want + distance)) return want + distance;
  }
  return null;
};

/** The room a block can grow or move into without touching its neighbours */
const roomInRow = (others: Placed[], block: Placed) => {
  const left = others.filter((other) => other.x + other.w <= block.x).reduce((max, other) => Math.max(max, other.x + other.w), 0);
  const right = others.filter((other) => other.x >= block.x + block.w).reduce((min, other) => Math.min(min, other.x), COLUMNS);
  return { left, right };
};

interface Props {
  value: MailTemplate;
  onChange: (template: MailTemplate) => void;
  uploadImage: (file: File) => Promise<string | null>;
  busy: boolean;
  /** The mail as the server rendered it (with the editor's marks), shown as the chosen mail program would */
  previewHtml: string;
  client: MailClient;
  onClientChange: (client: MailClient) => void;
}

export default function TemplateEditor({ value, onChange, uploadImage, busy, previewHtml, client, onClientChange }: Props) {
  const [t] = useTranslation("global");
  const blocks = place(value.blocks || []);
  const rowCount = blocks.length ? blocks[blocks.length - 1].row + 1 : 0;
  const [selected, setSelected] = useState<number | null>(null);
  const [target, setTarget] = useState<Target | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const [picking, setPicking] = useState<number | null>(null);
  const [layout, setLayout] = useState<Layout | null>(null);
  // Between a change and the server's new rendering the boxes sit on the old mail, so they are faded and inert
  const [stale, setStale] = useState(false);
  const frame = useRef<HTMLIFrameElement>(null);
  const overlay = useRef<HTMLDivElement>(null);
  const observer = useRef<ResizeObserver | null>(null);
  // History for undo and redo, as in the poster designer
  const history = useRef<{ list: string[]; at: number }>({ list: [JSON.stringify(value.blocks || [])], at: 0 });
  const [, bump] = useState(0);
  // The field the cursor was last in, so a clicked variable lands where the writer was working
  const { fieldProps, insert: insertVariable } = useFieldFocus();

  const names = (block: MailBlock) => {
    const name = t(`mails.blocks.${block.type}`);
    const excerpt = (block.type === "heading" || block.type === "text" || block.type === "imageText") && block.text ? `: ${block.text.replace(/\s+/g, " ").slice(0, 28)}` : "";
    return name + excerpt;
  };
  const placement = (block: Placed) => t("mails.placement", { from: block.x + 1, to: block.x + block.w, row: block.row + 1 });

  // ---------- changes, with history ----------
  const setBlocks = (next: MailBlock[], remember = true) => {
    const placed = place(next);
    onChange({ ...value, blocks: placed });
    setStale(true);
    if (!remember) return;
    const text = JSON.stringify(placed);
    const h = history.current;
    if (text === h.list[h.at]) return;
    h.list.splice(h.at + 1);
    h.list.push(text);
    if (h.list.length > 100) h.list.shift();
    else h.at += 1;
    bump((n) => n + 1);
  };
  const goTo = (at: number) => {
    const h = history.current;
    if (at < 0 || at >= h.list.length) return;
    h.at = at;
    onChange({ ...value, blocks: JSON.parse(h.list[at]) });
    setStale(true);
    setSelected(null);
    bump((n) => n + 1);
  };
  const change = (index: number, patch: Partial<MailBlock>) => setBlocks(blocks.map((block, at) => (at === index ? { ...block, ...patch } : block)));
  const remove = (index: number) => {
    setBlocks(blocks.filter((_, at) => at !== index));
    setSelected(null);
  };
  /** A new row at `line` (0 = above the first row) with one block; the rows below move down */
  const insertRow = (line: number, block: MailBlock, select = true) => {
    const shifted = blocks.map((other) => (other.row >= line ? { ...other, row: other.row + 1 } : other));
    const next = place([...shifted, { ...block, row: line, x: 0, w: block.w ?? COLUMNS }]);
    setBlocks(next);
    if (select) setSelected(next.findIndex((other) => other.row === line));
  };
  /** Into an existing row at column `x`, if there is room; otherwise a row of its own below */
  const insertInRow = (row: number, x: number, block: MailBlock) => {
    const w = Math.min(block.w ?? COLUMNS, COLUMNS);
    const slot = fitInRow(blocks.filter((other) => other.row === row), x, w);
    if (slot === null) {
      insertRow(row + 1, block);
      return;
    }
    const next = place([...blocks, { ...block, row, x: slot, w }]);
    setBlocks(next);
    setSelected(next.findIndex((other) => other.row === row && other.x === slot));
  };
  /** A block moves to a new row of its own at `line` */
  const moveToRow = (from: number, line: number) => {
    const block = blocks[from];
    const rest = blocks.filter((_, at) => at !== from).map((other) => (other.row >= line ? { ...other, row: other.row + 1 } : other));
    const next = place([...rest, { ...block, row: line, x: 0 }]);
    setBlocks(next);
    setSelected(next.findIndex((other) => other.row === line));
  };
  /** A block moves into another row, at the column nearest `x` with room for it */
  const moveInto = (from: number, row: number, x: number) => {
    const block = blocks[from];
    const others = blocks.filter((other, at) => at !== from && other.row === row);
    const slot = fitInRow(others, x, block.w);
    if (slot === null) return;
    const next = place(blocks.map((other, at) => (at === from ? { ...other, row, x: slot } : other)));
    setBlocks(next);
    setSelected(next.findIndex((other) => other.row === row && other.x === slot));
  };
  const moveRow = (index: number, direction: -1 | 1) => {
    const block = blocks[index];
    const to = block.row + direction;
    if (to < 0 || to >= rowCount) return;
    const others = blocks.filter((other, at) => at !== index && other.row === to);
    const slot = fitInRow(others, block.x, block.w);
    // No room in the neighbouring row: the block gets a row of its own on that side instead
    if (slot === null) moveToRow(index, direction < 0 ? to : to + 1);
    else moveInto(index, to, slot);
  };
  const resize = (index: number, w: number, fromLeft = false) => {
    const block = blocks[index];
    const others = blocks.filter((other, at) => at !== index && other.row === block.row);
    const { left, right } = roomInRow(others, block);
    const width = Math.max(1, Math.min(w, fromLeft ? block.x + block.w - left : right - block.x));
    change(index, fromLeft ? { x: block.x + block.w - width, w: width } : { w: width });
  };
  const nudge = (index: number, columns: number) => {
    const block = blocks[index];
    const others = blocks.filter((other, at) => at !== index && other.row === block.row);
    const { left, right } = roomInRow(others, block);
    change(index, { x: Math.max(left, Math.min(right - block.w, block.x + columns)) });
  };

  // ---------- the rendered mail is measured, so the boxes sit exactly on the elements ----------
  const measure = useCallback(() => {
    const doc = frame.current?.contentDocument;
    if (!doc?.body) return;
    const rect = (element: Element): Rect => {
      const r = element.getBoundingClientRect();
      return { left: r.left, top: r.top, width: r.width, height: r.height };
    };
    const gridElement = doc.querySelector("[data-grid]");
    const gridRect = gridElement ? rect(gridElement) : { left: 0, top: 0, width: doc.body.clientWidth, height: 0 };
    const padding = gridElement ? parseFloat(doc.defaultView?.getComputedStyle(gridElement).paddingLeft || "0") || 0 : 0;
    const measured: Layout = {
      height: Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight, 200),
      grid: { left: gridRect.left + padding, width: Math.max(1, gridRect.width - 2 * padding), top: gridRect.top + padding, bottom: gridRect.top + gridRect.height - padding },
      blocks: {},
      rows: [],
    };
    doc.querySelectorAll("[data-block]").forEach((element) => {
      measured.blocks[Number(element.getAttribute("data-block"))] = rect(element);
    });
    doc.querySelectorAll("tr[data-row]").forEach((element) => {
      measured.rows[Number(element.getAttribute("data-row"))] = rect(element);
    });
    setLayout((current) => (JSON.stringify(current) === JSON.stringify(measured) ? current : measured));
  }, []);
  const loaded = () => {
    setStale(false);
    measure();
    observer.current?.disconnect();
    const doc = frame.current?.contentDocument;
    if (doc?.body && typeof ResizeObserver !== "undefined") {
      observer.current = new ResizeObserver(() => measure());
      observer.current.observe(doc.body);
    }
    // Pictures change the height when they arrive
    doc?.querySelectorAll("img").forEach((img) => img.addEventListener("load", measure));
  };
  useEffect(() => () => observer.current?.disconnect(), []);
  // Should the server not answer, the boxes come back after a moment anyway
  useEffect(() => {
    if (!stale) return undefined;
    const timer = setTimeout(() => setStale(false), 3000);
    return () => clearTimeout(timer);
  }, [stale]);

  // ---------- where the pointer is, in grid terms ----------
  const columnAt = (clientX: number) => {
    const box = overlay.current?.getBoundingClientRect();
    if (!box || !layout) return 0;
    const x = clientX - box.left - layout.grid.left;
    return Math.max(0, Math.min(COLUMNS - 1, Math.floor((x / layout.grid.width) * COLUMNS)));
  };
  /** The row under the pointer, or the line between two rows (above, between or below them) */
  const rowAt = (clientY: number): { row: number } | { line: number } => {
    const box = overlay.current?.getBoundingClientRect();
    if (!box || !layout || !layout.rows.length) return { line: 0 };
    const y = clientY - box.top;
    const rows = layout.rows;
    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];
      if (!row) continue;
      if (y < row.top) return { line: index };
      // The lowest and highest fifth of a row counts as the line next to it, so a new row is easy to make
      if (y <= row.top + row.height) {
        if (row.height > 60 && y < row.top + row.height * 0.2) return { line: index };
        if (row.height > 60 && y > row.top + row.height * 0.8) return { line: index + 1 };
        return { row: index };
      }
    }
    return { line: rows.length };
  };
  const targetFor = (clientX: number, clientY: number, w: number, from: number | null): Target | null => {
    if (!layout) return null;
    const at = rowAt(clientY);
    if ("line" in at) return at;
    const block = from !== null ? blocks[from] : null;
    if (block?.type === "imageText") return { line: at.row + 1 };
    const others = blocks.filter((other, index) => index !== from && other.row === at.row);
    const slot = fitInRow(others, columnAt(clientX) - Math.floor(w / 2), w);
    if (slot === null) return { line: at.row + 1 };
    return { row: at.row, x: slot, w };
  };
  const applyTarget = (where: Target, from: number | null, block?: MailBlock) => {
    if (from !== null) {
      if ("line" in where) moveToRow(from, where.line);
      else if (where.row === blocks[from].row) change(from, { x: where.x });
      else moveInto(from, where.row, where.x);
      return;
    }
    if (!block) return;
    if ("line" in where || block.type === "imageText") insertRow("line" in where ? where.line : where.row + 1, block);
    else insertInRow(where.row, where.x, { ...block, w: where.w });
  };

  // ---------- dragging a box: moves the element; dragging its edge: changes the width ----------
  const startDrag = (event: React.PointerEvent, index: number, side?: "left" | "right") => {
    if (event.button !== 0 || !layout) return;
    event.preventDefault();
    event.stopPropagation();
    const element = event.currentTarget as HTMLElement;
    element.setPointerCapture(event.pointerId);
    // preventDefault stops the browser from focusing the box, so it is focused by hand (the keyboard works on it)
    (side ? element.parentElement : element)?.focus({ preventScroll: true });
    const start = blocks[index];
    const startX = event.clientX;
    const columnPx = layout.grid.width / COLUMNS;
    let moved = false;
    let where: Target | null = null;
    let width = start.w;
    const move = (pointer: PointerEvent) => {
      if (!moved && Math.hypot(pointer.clientX - startX, pointer.clientY - event.clientY) < 4) return;
      moved = true;
      if (side) {
        const delta = Math.round((pointer.clientX - startX) / columnPx);
        width = side === "right" ? start.w + delta : start.w - delta;
        resize(index, width, side === "left");
        return;
      }
      setDragging(index);
      where = targetFor(pointer.clientX, pointer.clientY, start.w, index);
      setTarget(where);
    };
    const stop = () => {
      element.removeEventListener("pointermove", move);
      element.removeEventListener("pointerup", stop);
      element.removeEventListener("pointercancel", stop);
      setDragging(null);
      setTarget(null);
      if (!moved) {
        setSelected(index);
        return;
      }
      if (!side && where) applyTarget(where, index);
    };
    element.addEventListener("pointermove", move);
    element.addEventListener("pointerup", stop);
    element.addEventListener("pointercancel", stop);
  };

  // ---------- the browser's own drag and drop: a new element from the list, or a picture from the desktop ----------
  const dragOver = (event: React.DragEvent) => {
    if (!event.dataTransfer.types.includes(BLOCK_KIND) && !event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    setTarget(targetFor(event.clientX, event.clientY, 6, null));
  };
  const drop = async (event: React.DragEvent) => {
    event.preventDefault();
    const where = target || targetFor(event.clientX, event.clientY, 6, null) || { line: rowCount };
    setTarget(null);
    const type = event.dataTransfer.getData(BLOCK_KIND) as MailBlockType;
    const file = event.dataTransfer.files?.[0];
    if (type) applyTarget(where, null, newBlock(type, t));
    else if (file?.type?.startsWith("image/")) {
      const url = await uploadImage(file);
      if (url) applyTarget(where, null, { type: "image", imageUrl: url, align: "left" });
    }
  };

  // ---------- keyboard on a box: arrows move, Shift + arrows change the width, Delete removes ----------
  const keys = (event: React.KeyboardEvent, index: number) => {
    const block = blocks[index];
    if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault();
      remove(index);
    } else if (event.key === "Escape") {
      setSelected(null);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setSelected(index);
    } else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      const step = event.key === "ArrowLeft" ? -1 : 1;
      if (event.shiftKey) resize(index, block.w + step);
      else nudge(index, step);
    } else if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      moveRow(index, event.key === "ArrowUp" ? -1 : 1);
    }
  };
  // Undo and redo with the keyboard, not while writing in a field
  const rootKeys = (event: React.KeyboardEvent) => {
    if (!(event.ctrlKey || event.metaKey)) return;
    if ((event.target as HTMLElement).closest("input, textarea, select, [contenteditable]")) return;
    const key = event.key.toLowerCase();
    if (key === "z" && !event.shiftKey) {
      event.preventDefault();
      goTo(history.current.at - 1);
    } else if (key === "y" || (key === "z" && event.shiftKey)) {
      event.preventDefault();
      goTo(history.current.at + 1);
    }
  };

  const focusBox = (index: number) => overlay.current?.querySelector<HTMLElement>(`[data-testid="block-${index}"]`)?.focus({ preventScroll: false });

  // ---------- the settings of the chosen element ----------
  const pick = async (index: number, file?: File) => {
    if (!file) return;
    const url = await uploadImage(file);
    if (url) change(index, { imageUrl: url });
  };
  const alignment = (index: number, block: MailBlock) => (
    <ToggleButtonGroup size="small" exclusive value={block.align || "left"} onChange={(_event, next) => next && change(index, { align: next })} aria-label={t("mails.align")}>
      <ToggleButton value="left" aria-label={t("mails.alignLeft")}>
        <FormatAlignLeft fontSize="small" />
      </ToggleButton>
      <ToggleButton value="center" aria-label={t("mails.alignCenter")}>
        <FormatAlignCenter fontSize="small" />
      </ToggleButton>
      <ToggleButton value="right" aria-label={t("mails.alignRight")}>
        <FormatAlignRight fontSize="small" />
      </ToggleButton>
    </ToggleButtonGroup>
  );
  const picture = (index: number, block: MailBlock) => (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
      {block.imageUrl && <Box component="img" src={block.imageUrl} alt="" sx={{ width: "100%", maxHeight: 140, objectFit: "cover", borderRadius: 1 }} />}
      {/* A picture that is already uploaded is taken from Media instead of being uploaded again */}
      <Button variant="outlined" size="small" onClick={() => setPicking(index)}>
        {t("media.pick")}
      </Button>
      <Button variant="outlined" component="label" size="small" disabled={busy}>
        {block.imageUrl ? t("mails.changeImage") : t("mails.addImage")}
        <input hidden type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(event) => pick(index, event.target.files?.[0])} />
      </Button>
      {block.imageUrl && (
        <Button variant="outlined" size="small" onClick={() => change(index, { imageUrl: "" })}>
          {t("mails.removeImage")}
        </Button>
      )}
    </Box>
  );
  const widthLabel = (w: number) => {
    if (w === COLUMNS) return t("mails.widthFull");
    if (w === 6) return t("mails.widthHalf");
    if (w === 4) return t("mails.widthThird");
    if (w === 8) return t("mails.widthTwoThirds");
    return t("mails.columns", { w });
  };
  const fields = (index: number, block: Placed) => {
    switch (block.type) {
      case "heading":
      case "text":
        return (
          <>
            <TextField fullWidth multiline minRows={block.type === "text" ? 4 : 2} size="small" inputProps={{ "aria-label": t(`mails.blocks.${block.type}`) }} value={block.text ?? ""} onChange={(event) => change(index, { text: event.target.value })} {...fieldProps} />
            {alignment(index, block)}
          </>
        );
      case "image":
        return (
          <>
            {picture(index, block)}
            <TextField fullWidth size="small" label={t("mails.altText")} helperText={t("mails.altHelp")} value={block.alt ?? ""} onChange={(event) => change(index, { alt: event.target.value })} {...fieldProps} />
          </>
        );
      case "imageText":
        return (
          <>
            {picture(index, block)}
            <TextField fullWidth multiline minRows={4} size="small" inputProps={{ "aria-label": t("mails.blocks.imageText") }} value={block.text ?? ""} onChange={(event) => change(index, { text: event.target.value })} {...fieldProps} />
            <TextField select size="small" fullWidth label={t("mails.imageSide")} value={block.size === "right" ? "right" : "left"} onChange={(event) => change(index, { size: event.target.value })}>
              <MenuItem value="left">{t("mails.imageLeft")}</MenuItem>
              <MenuItem value="right">{t("mails.imageRight")}</MenuItem>
            </TextField>
          </>
        );
      case "button":
        return (
          <>
            <TextField fullWidth size="small" label={t("mails.buttonText")} value={block.text ?? ""} onChange={(event) => change(index, { text: event.target.value })} {...fieldProps} />
            <TextField fullWidth size="small" label={t("mails.buttonUrl")} value={block.url ?? ""} onChange={(event) => change(index, { url: event.target.value })} {...fieldProps} />
            {alignment(index, block)}
          </>
        );
      case "spacer":
        return (
          <TextField select size="small" fullWidth label={t("mails.spacerSize")} value={block.size || "medium"} onChange={(event) => change(index, { size: event.target.value })}>
            <MenuItem value="small">{t("mails.spacerSmall")}</MenuItem>
            <MenuItem value="medium">{t("mails.spacerMedium")}</MenuItem>
            <MenuItem value="large">{t("mails.spacerLarge")}</MenuItem>
          </TextField>
        );
      default:
        return (
          <Typography variant="body2" color="text.secondary">
            {t("mails.dividerHelp")}
          </Typography>
        );
    }
  };

  const panel = () => {
    const block = selected !== null ? blocks[selected] : undefined;
    if (selected === null || !block) {
      return (
        <>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {t("mails.elements")}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
            {t("mails.elementsHelp")}
          </Typography>
          <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, mb: 1 }}>
            {blocks.map((other, index) => (
              <Box component="li" key={index}>
                <Button
                  fullWidth
                  size="small"
                  data-testid={`list-${index}`}
                  onClick={() => {
                    setSelected(index);
                    focusBox(index);
                  }}
                  startIcon={ELEMENTS.find((element) => element.type === other.type)?.icon}
                  sx={{ justifyContent: "flex-start", textTransform: "none", color: "text.primary", fontWeight: 400, "& .MuiButton-startIcon": { color: "text.secondary" } }}
                >
                  <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {names(other)}
                  </Box>
                </Button>
              </Box>
            ))}
            {!blocks.length && (
              <Typography variant="body2" color="text.secondary">
                {t("mails.noElements")}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" component="p">
            {t("mails.gridHelp")}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
            {t("mails.keyboardHelp")}
          </Typography>
        </>
      );
    }
    const resizable = block.type !== "imageText";
    return (
      <>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            {t(`mails.blocks.${block.type}`)}
          </Typography>
          <IconButton size="small" onClick={() => setSelected(null)} aria-label={t("mails.close")}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {fields(selected, block)}
          {resizable && (
            <TextField select size="small" fullWidth label={t("mails.width")} value={block.w} onChange={(event) => resize(selected, Number(event.target.value))} SelectProps={{ SelectDisplayProps: { "aria-label": t("mails.width") } as React.HTMLAttributes<HTMLDivElement> }}>
              {(WIDTHS.includes(block.w) ? WIDTHS : [...WIDTHS, block.w].sort((a, b) => b - a)).map((w) => (
                <MenuItem key={w} value={w}>
                  {widthLabel(w)}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            <Tooltip title={t("mails.moveUp")}>
              <span>
                <IconButton size="small" disabled={block.row === 0} onClick={() => moveRow(selected, -1)} aria-label={`${t("mails.moveUp")} ${selected + 1}`}>
                  <ArrowUpward fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t("mails.moveDown")}>
              <span>
                <IconButton size="small" disabled={block.row === rowCount - 1} onClick={() => moveRow(selected, 1)} aria-label={`${t("mails.moveDown")} ${selected + 1}`}>
                  <ArrowDownward fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t("mails.duplicate")}>
              <IconButton size="small" onClick={() => insertRow(block.row + 1, { ...block })} aria-label={`${t("mails.duplicate")} ${selected + 1}`}>
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("mails.removeBlock")}>
              <IconButton size="small" onClick={() => remove(selected)} aria-label={`${t("mails.removeBlock")} ${selected + 1}`}>
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {placement(block)}
          </Typography>
        </Box>
      </>
    );
  };

  // ---------- the boxes over the mail ----------
  const box = (index: number, block: Placed, rect: Rect) => {
    const isSelected = selected === index;
    const resizable = block.type !== "imageText";
    const handle = (side: "left" | "right") => (
      <Box
        role="separator"
        aria-label={t("mails.resize")}
        aria-orientation="vertical"
        onPointerDown={(event) => startDrag(event, index, side)}
        sx={{ position: "absolute", top: "50%", [side]: -6, width: 10, height: 28, mt: "-14px", borderRadius: 1, bgcolor: ORANGE, border: "2px solid #fff", cursor: "ew-resize", touchAction: "none", boxShadow: 1 }}
      />
    );
    return (
      <Box
        key={index}
        role="button"
        tabIndex={0}
        data-testid={`block-${index}`}
        aria-label={`${names(block)}, ${placement(block)}`}
        aria-pressed={isSelected}
        onPointerDown={(event) => startDrag(event, index)}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => keys(event, index)}
        sx={{
          position: "absolute",
          left: rect.left - 4,
          top: rect.top - 4,
          width: rect.width + 8,
          height: rect.height + 8,
          borderRadius: 1.5,
          border: isSelected ? `2px solid ${ORANGE}` : "1px dashed rgba(235,102,8,0.45)",
          bgcolor: isSelected ? "rgba(235,102,8,0.06)" : "transparent",
          cursor: dragging === index ? "grabbing" : "grab",
          touchAction: "none",
          opacity: dragging === index ? 0.5 : stale ? 0.35 : 1,
          pointerEvents: stale ? "none" : "auto",
          outline: "none",
          "&:hover": { border: `1px solid ${ORANGE}` },
          "&:focus-visible": { boxShadow: `0 0 0 3px rgba(235,102,8,0.3)` },
          "&:hover .tag, &[aria-pressed=true] .tag": { opacity: 1 },
        }}
      >
        <Box
          className="tag"
          sx={{ position: "absolute", top: -1, left: -1, px: 0.75, py: 0.1, fontSize: 11, fontWeight: 600, color: "#fff", bgcolor: ORANGE, borderRadius: "6px 0 6px 0", opacity: isSelected ? 1 : 0, transition: "opacity .12s", pointerEvents: "none", whiteSpace: "nowrap", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {t(`mails.blocks.${block.type}`)}
        </Box>
        {isSelected && resizable && handle("left")}
        {isSelected && resizable && handle("right")}
      </Box>
    );
  };

  /** Where the dragged element would land: a box in a row, or a bar on the line between two rows */
  const ghost = () => {
    if (!target || !layout) return null;
    const columnPx = layout.grid.width / COLUMNS;
    if ("line" in target) {
      const rows = layout.rows;
      const above = target.line > 0 ? rows[target.line - 1] : undefined;
      const below = rows[target.line];
      const y = above && below ? (above.top + above.height + below.top) / 2 : above ? above.top + above.height + 3 : below ? below.top - 3 : layout.grid.top;
      return (
        <Box aria-hidden sx={{ position: "absolute", left: layout.grid.left, width: layout.grid.width, top: y - 3, height: 6, bgcolor: ORANGE, borderRadius: 3, pointerEvents: "none", boxShadow: 1 }}>
          <Box sx={{ position: "absolute", left: "50%", top: -10, transform: "translateX(-50%)", bgcolor: ORANGE, color: "#fff", fontSize: 11, fontWeight: 600, px: 1, py: 0.25, borderRadius: 1, whiteSpace: "nowrap" }}>{t("mails.newRow")}</Box>
        </Box>
      );
    }
    const row = layout.rows[target.row];
    if (!row) return null;
    return <Box aria-hidden sx={{ position: "absolute", left: layout.grid.left + target.x * columnPx, width: target.w * columnPx, top: row.top - 4, height: row.height + 8, border: `2px dashed ${ORANGE}`, borderRadius: 1.5, bgcolor: "rgba(235,102,8,0.12)", pointerEvents: "none" }} />;
  };

  const history_ = history.current;
  return (
    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }} onKeyDown={rootKeys}>
      <Dialog open={picking !== null} onClose={() => setPicking(null)} fullWidth maxWidth="md">
        <DialogTitle>{t("media.pickTitle")}</DialogTitle>
        <DialogContent>
          <MediaLibrary
            onPick={(asset) => {
              if (picking !== null) change(picking, { imageUrl: asset.url });
              setPicking(null);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPicking(null)}>{t("media.cancel")}</Button>
        </DialogActions>
      </Dialog>

      {/* The elements you build with: click puts one at the bottom, drag puts it where it lands */}
      <Box sx={{ flex: "0 0 160px", minWidth: 150 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t("mails.elements")}
        </Typography>
        {ELEMENTS.map((element) => (
          <Paper
            key={element.type}
            variant="outlined"
            draggable
            data-testid={`palette-${element.type}`}
            onDragStart={(event) => event.dataTransfer.setData(BLOCK_KIND, element.type)}
            onClick={() => insertRow(rowCount, newBlock(element.type, t))}
            sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, mb: 1, cursor: "grab", "&:hover": { borderColor: "primary.main" } }}
          >
            {element.icon}
            <Typography variant="body2">{t(`mails.blocks.${element.type}`)}</Typography>
          </Paper>
        ))}
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
          {t("mails.dragHelp")}
        </Typography>
      </Box>

      {/* The mail itself is the canvas */}
      <Box sx={{ flex: "1 1 520px", minWidth: 360 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Tabs value={client} onChange={(_event, next) => onClientChange(next)} variant="scrollable" scrollButtons={false} sx={{ minHeight: 36, flex: 1, "& .MuiTab-root": { minHeight: 36, py: 0, textTransform: "none" } }}>
            {MAIL_CLIENTS.map((name) => (
              <Tab key={name} value={name} label={t(`mails.clients.${name}`)} />
            ))}
          </Tabs>
          <Tooltip title={`${t("mails.undo")} (Ctrl+Z)`}>
            <span>
              <IconButton size="small" disabled={history_.at === 0} onClick={() => goTo(history_.at - 1)} aria-label={t("mails.undo")}>
                <Undo fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={`${t("mails.redo")} (Ctrl+Y)`}>
            <span>
              <IconButton size="small" disabled={history_.at >= history_.list.length - 1} onClick={() => goTo(history_.at + 1)} aria-label={t("mails.redo")}>
                <Redo fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {t(`mails.clientHelp.${client}`)}
        </Typography>
        {clippedByGmail(previewHtml, client) && (
          <Typography variant="caption" color="warning.main" sx={{ display: "block", mb: 1 }}>
            {t("mails.gmailClipped")}
          </Typography>
        )}
        <Box
          data-testid="canvas"
          onClick={() => setSelected(null)}
          sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "auto", height: 640, bgcolor: "#f6f5f3", display: "flex", justifyContent: "center", alignItems: "flex-start" }}
        >
          <Box
            sx={{
              width: clientWidth[client],
              maxWidth: "100%",
              flex: "0 0 auto",
              position: "relative",
              bgcolor: "#f6f5f3",
              ...(isPhone(client) ? { border: "8px solid #323232", borderRadius: 4, my: 1 } : {}),
            }}
          >
            <iframe ref={frame} title={t("mails.preview")} srcDoc={previewHtml} onLoad={loaded} style={{ width: "100%", height: layout?.height ?? 600, border: 0, display: "block", pointerEvents: "none" }} />
            <Box ref={overlay} onDragOver={dragOver} onDragLeave={() => setTarget(null)} onDrop={drop} sx={{ position: "absolute", left: 0, top: 0, width: "100%", height: layout?.height ?? 600 }}>
              {/* The 12 columns, drawn faintly, like the poster designer's grid */}
              {layout && (
                <Box
                  aria-hidden
                  sx={{
                    position: "absolute",
                    left: layout.grid.left,
                    width: layout.grid.width,
                    top: layout.grid.top,
                    height: Math.max(0, layout.grid.bottom - layout.grid.top),
                    pointerEvents: "none",
                    backgroundImage: `repeating-linear-gradient(to right, rgba(235,102,8,0.14) 0, rgba(235,102,8,0.14) 1px, transparent 1px, transparent calc(100% / ${COLUMNS}))`,
                    borderRight: "1px solid rgba(235,102,8,0.14)",
                  }}
                />
              )}
              {layout && blocks.map((block, index) => (layout.blocks[index] ? box(index, block, layout.blocks[index]) : null))}
              {ghost()}
              {!blocks.length && layout && (
                // The empty mail takes the first element itself
                <Paper
                  variant="outlined"
                  data-testid="empty-mail"
                  onDragOver={dragOver}
                  onDrop={drop}
                  sx={{
                    position: "absolute",
                    left: layout.grid.left,
                    width: layout.grid.width,
                    top: layout.grid.top + 70,
                    p: 4,
                    textAlign: "center",
                    borderStyle: "dashed",
                    borderWidth: 2,
                    borderColor: target ? "primary.main" : "divider",
                    bgcolor: target ? "rgba(235,102,8,0.08)" : "rgba(255,255,255,0.9)",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t("mails.emptyMail")}
                  </Typography>
                </Paper>
              )}
            </Box>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          {t("mails.previewDisclaimer")}
        </Typography>
      </Box>

      {/* The settings of the chosen element, or the list of them */}
      <Box sx={{ flex: "0 0 260px", minWidth: 240 }} data-testid="props">
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          {panel()}
        </Paper>
        <VariablePalette variables={VARIABLES.map((key) => ({ key }))} onInsert={insertVariable} usedIn={blocks.map((block) => `${block.text || ""} ${block.url || ""}`).join(" ")} />
      </Box>
    </Box>
  );
}
